"use client";

import Image from "next/image";
import * as React from "react";

import {
  BOTTLENECK,
  createBottleneckEngine,
  type BottleneckEvent,
} from "@/components/site/sections/bottleneck-canvas";
import {
  ECHO,
  PINGPONG,
  createEchoEngine,
  createPingPongEngine,
  type SceneEngine,
  type SceneEvent,
} from "@/components/site/sections/founder-scenes";
import {
  founderMapVisibility,
  founderStepRanOut,
  type FounderMapVisibility,
} from "@/components/site/sections/founder-visibility";
import {
  contextStoryCast,
  founderBreakPatterns,
  type ContextStoryPersonId,
  type FounderBreakPattern,
} from "@/lib/content/home-context-story";
import { cn } from "@/lib/utils";

const STEP_MS = 5500;

/** Which route lights up when this person asks; taken modulo the route count. */
const castRoute: Record<ContextStoryPersonId, number> = {
  priya: 9,
  adrian: 4,
  james: 27,
  serena: 33,
  julian: 18,
};

/** The askers' routes on the flow step, in the order their questions arrive. */
const flowAskerRoutes = (founderBreakPatterns.find((pattern) => pattern.visual === "flow")?.messages ?? []).map(
  (message) => castRoute[message.from],
);

function createFlowEngine(canvas: HTMLCanvasElement, emit: (event: BottleneckEvent) => void) {
  return createBottleneckEngine(canvas, (count) => flowAskerRoutes.map((route) => route % count), emit);
}

/** The flow step's question card: which question just landed, how fast they come, and whether the founder gave out. */
type FlowBeat = { asked: number; interval: number; burnt: boolean };

const FLOW_START: FlowBeat = { asked: 0, interval: 1, burnt: false };

function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);

    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, [query]);

  return matches;
}

export function FounderRoutingMap() {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [userPicked, setUserPicked] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [visibility, setVisibility] = React.useState<FounderMapVisibility>("out");
  // Set once the visitor reaches the block; cleared when it leaves the screen, so every visit starts at step 1.
  const [started, setStarted] = React.useState(false);
  // Bumped on every new visit to remount the scenes and the progress bar from their first frame.
  const [visit, setVisit] = React.useState(0);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [echoIndex, setEchoIndex] = React.useState<number | null>(null);
  const [flowBeat, setFlowBeat] = React.useState<FlowBeat>(FLOW_START);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const idBase = React.useId();
  const active = founderBreakPatterns[activeIndex];
  const playing = !userPicked && !reducedMotion;
  const live = started && (visibility === "reached" || visibility === "watching");
  const running = playing && live && !hovered && !focused;

  React.useEffect(() => {
    const node = rootRef.current;

    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) =>
        setVisibility(
          founderMapVisibility({
            intersecting: entry.isIntersecting,
            ratio: entry.intersectionRatio,
            visibleHeight: entry.intersectionRect.height,
            viewportHeight: entry.rootBounds?.height ?? window.innerHeight,
          }),
        ),
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (visibility === "reached" && !started) setStarted(true);

    if (visibility === "out" && started) {
      setStarted(false);
      setVisit((current) => current + 1);
      setEchoIndex(null);
      setFlowBeat(FLOW_START);
      // A tab the visitor picked stays picked; the autoplay story goes back to its start.
      if (!userPicked) setActiveIndex(0);
    }
  }, [visibility, started, userPicked]);

  const pick = (index: number, focus = false) => {
    const next = (index + founderBreakPatterns.length) % founderBreakPatterns.length;
    setUserPicked(true);
    setStarted(true);
    setActiveIndex(next);
    if (focus) tabRefs.current[next]?.focus();
  };

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const keyMoves: Record<string, number> = {
      ArrowRight: activeIndex + 1,
      ArrowDown: activeIndex + 1,
      ArrowLeft: activeIndex - 1,
      ArrowUp: activeIndex - 1,
      Home: 0,
      End: founderBreakPatterns.length - 1,
    };

    if (event.key in keyMoves) {
      event.preventDefault();
      pick(keyMoves[event.key], true);
    }
  };

  const panelId = `${idBase}-panel`;
  const tabId = (index: number) => `${idBase}-tab-${index}`;

  return (
    <div
      ref={rootRef}
      className="overflow-hidden rounded-[1.75rem] bg-[#0b0b0d] text-white"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocused(false);
        }
      }}
    >
      <div
        role="tablist"
        aria-label="Where founder-held context breaks"
        className="grid grid-cols-4 gap-2 px-5 pt-5 md:gap-4 md:px-8 md:pt-8"
      >
        {founderBreakPatterns.map((pattern, index) => {
          const selected = index === activeIndex;
          const done = index < activeIndex;

          return (
            <button
              key={pattern.id}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              id={tabId(index)}
              aria-selected={selected}
              aria-controls={panelId}
              tabIndex={selected ? 0 : -1}
              onClick={() => pick(index)}
              onKeyDown={onTabKeyDown}
              className="group self-start rounded-md pb-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-4 focus-visible:ring-offset-[#0b0b0d]"
            >
              <span className="relative block h-[3px] overflow-hidden rounded-full bg-white/15">
                <span
                  key={selected ? `run-${visit}-${activeIndex}-${playing}` : "idle"}
                  className={cn(
                    "absolute inset-0 origin-left rounded-full bg-white",
                    !selected && !done && "scale-x-0",
                  )}
                  style={
                    selected && playing
                      ? {
                          animation: `cooperly-cycle-progress ${STEP_MS}ms linear forwards`,
                          animationPlayState: running ? "running" : "paused",
                        }
                      : undefined
                  }
                  onAnimationEnd={
                    selected && playing
                      ? (event) => {
                          if (!founderStepRanOut(event.elapsedTime)) return;
                          setActiveIndex((current) => (current + 1) % founderBreakPatterns.length);
                        }
                      : undefined
                  }
                />
              </span>
              <span
                className={cn(
                  "mt-3 hidden text-sm font-medium leading-5 transition-colors duration-200 sm:block",
                  selected ? "text-white" : "text-white/50 group-hover:text-white/75",
                )}
              >
                {pattern.title}
              </span>
              <span className="sr-only sm:hidden">{pattern.title}</span>
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={panelId}
        aria-labelledby={tabId(activeIndex)}
        className="px-5 pt-4 pb-5 md:px-8 md:pb-7"
      >
        <p
          key={`title-${active.id}`}
          className="cooperly-swap-in text-base font-semibold leading-6 text-white sm:hidden"
        >
          {active.title}
        </p>
        <div className="sr-only">
          <p>{active.explanation}</p>
          <ul>
            {active.messages.map((message, index) => (
              <li key={`${message.from}-${index}`}>
                {contextStoryCast[message.from].name} to you: “{message.text}”
              </li>
            ))}
          </ul>
          <p>{active.meta}</p>
        </div>

        <div className="relative mt-2 aspect-[4/5] w-full select-none sm:mt-6 sm:aspect-[2/1]" aria-hidden="true">
          {active.visual === "flow" ? (
            <BottleneckStage
              key={`flow-${visit}`}
              pattern={active}
              beat={flowBeat}
              onBeat={setFlowBeat}
              animate={live && !reducedMotion}
              reducedMotion={reducedMotion}
            />
          ) : active.visual === "flood" ? (
            <QuestionFlood
              key={`${active.id}-${visit}`}
              pattern={active}
              ticking={live && !reducedMotion && !hovered && !focused}
              reducedMotion={reducedMotion}
            />
          ) : active.visual === "pingpong" ? (
            <PingPongScene
              key={`${active.id}-${visit}`}
              pattern={active}
              animate={live && !reducedMotion}
              reducedMotion={reducedMotion}
            />
          ) : (
            <EchoScene
              key={`${active.id}-${visit}`}
              pattern={active}
              animate={live && !reducedMotion}
              reducedMotion={reducedMotion}
              onEcho={setEchoIndex}
            />
          )}
        </div>

        {active.visual === "flow" ? (
          <div className="mt-4 sm:hidden" aria-hidden="true">
            <FlowQuestion pattern={active} beat={flowBeat} still={reducedMotion} />
          </div>
        ) : null}
        {active.visual === "pingpong" ? (
          <div className="mt-4 sm:hidden" aria-hidden="true">
            <QuestionCards pattern={active} />
          </div>
        ) : null}
        {active.visual === "echo" ? (
          <div className="mt-4 lg:hidden" aria-hidden="true">
            <EchoCard pattern={active} index={reducedMotion ? 1 : (echoIndex ?? active.messages.length - 1)} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function BottleneckStage({
  pattern,
  beat,
  onBeat,
  animate,
  reducedMotion,
}: {
  pattern: FounderBreakPattern;
  beat: FlowBeat;
  onBeat: React.Dispatch<React.SetStateAction<FlowBeat>>;
  animate: boolean;
  reducedMotion: boolean;
}) {
  const percent = (value: number) => `${value * 100}%`;

  // Each visit starts a fresh cycle, before the first frame paints.
  React.useLayoutEffect(() => {
    onBeat(FLOW_START);
  }, [onBeat]);

  const canvasRef = useSceneCanvas(createFlowEngine, animate, reducedMotion, (event: BottleneckEvent) => {
    if (event.type === "reset") onBeat(FLOW_START);
    if (event.type === "hit") onBeat({ asked: event.count, interval: event.interval, burnt: false });
    if (event.type === "burst") onBeat((current) => ({ ...current, burnt: true }));
  });

  return (
    <div className="cooperly-swap-in absolute inset-0">
      <canvas ref={canvasRef} className="absolute inset-0 size-full" />

      {/* Clears the founder's capacity ring, which the canvas sizes from the stage width. */}
      <span
        className="absolute -translate-y-1/2 text-sm font-medium text-white [--ring-gap:1.25rem] sm:text-lg sm:[--ring-gap:1.75rem]"
        style={{ left: `calc(${percent(BOTTLENECK.founder.x)} + var(--ring-gap))`, top: percent(BOTTLENECK.founder.y) }}
      >
        You
      </span>

      <div
        className="absolute hidden w-[17rem] sm:block"
        style={{
          left: percent(BOTTLENECK.neck.x - 0.06),
          bottom: percent(1 - BOTTLENECK.neck.y + 0.17),
        }}
      >
        <FlowQuestion pattern={pattern} beat={beat} still={reducedMotion} />
      </div>
    </div>
  );
}

/** Shades of the questions still waiting behind the current one, nearest first. */
const waitingShades = ["#d4d4d8", "#a1a1aa", "#71717a"];

/**
 * The question that just reached the founder. It flips to the next one on every
 * hit, quicker as they speed up, while the ones still waiting stack up behind it.
 * When the founder gives out, the card drops to an outline with the rest of the chain.
 */
function FlowQuestion({ pattern, beat, still }: { pattern: FounderBreakPattern; beat: FlowBeat; still: boolean }) {
  const shown = still ? { asked: 0, interval: 1, burnt: false } : beat;
  const message = pattern.messages[shown.asked % pattern.messages.length];
  const person = contextStoryCast[message.from];
  const behind = still ? 2 : Math.min(waitingShades.length, Math.floor(shown.asked / 3));
  // The swap never takes longer than the gap before the next question.
  const swapMs = Math.round(Math.min(260, Math.max(60, shown.interval * 600)));

  return (
    <div className="relative">
      {waitingShades
        .map((shade, depth) => (
          <div
            key={shade}
            className="absolute inset-x-0 top-0 h-full rounded-2xl transition-[opacity,transform] duration-300 ease-[var(--ease-out)]"
            style={{
              background: shade,
              transform: `translateY(${-(depth + 1) * 6}px) scale(${1 - (depth + 1) * 0.045})`,
              opacity: depth < behind && !shown.burnt ? 1 : 0,
            }}
          />
        ))
        .reverse()}
      <div
        key={shown.asked}
        className={cn(
          "relative flex min-h-[5.125rem] items-start gap-3 rounded-2xl border px-3.5 py-3 transition-[background-color,border-color,color,box-shadow] duration-500 ease-[var(--ease-out)]",
          shown.burnt
            ? "border-white/15 bg-transparent text-white/50 shadow-none"
            : "border-white bg-white text-foreground shadow-[0_24px_48px_-24px_rgba(0,0,0,0.9)]",
        )}
        style={still ? undefined : { animation: `cooperly-flow-swap ${swapMs}ms var(--ease-out) both` }}
      >
        <CastAvatar person={message.from} muted={shown.burnt} className="duration-500" />
        <span className="min-w-0">
          <span
            className={cn(
              "block text-xs font-medium leading-4 transition-colors duration-500",
              shown.burnt ? "text-white/40" : "text-muted-foreground",
            )}
          >
            {person.name}
          </span>
          <span className="mt-0.5 block text-sm font-medium leading-5">“{message.text}”</span>
        </span>
      </div>
    </div>
  );
}

function QuestionCards({ pattern }: { pattern: FounderBreakPattern }) {
  return (
    <div key={pattern.id} className="cooperly-swap-in flex flex-col gap-2">
      {pattern.messages.map((message, index) => {
        const person = contextStoryCast[message.from];

        return (
          <div
            key={`${message.from}-${index}`}
            className="flex items-start gap-3 rounded-2xl bg-white px-3.5 py-3 text-foreground shadow-[0_24px_48px_-24px_rgba(0,0,0,0.9)]"
          >
            <CastAvatar person={message.from} />
            <span className="min-w-0">
              <span className="block text-xs font-medium leading-4 text-muted-foreground">
                {person.name}
              </span>
              <span className="mt-0.5 block text-sm font-medium leading-5">“{message.text}”</span>
            </span>
          </div>
        );
      })}
      <p className="px-1 text-xs leading-4 text-white/55">{pattern.meta}</p>
    </div>
  );
}

/** Phone view of 04: the question that just came back, stacked so the height never jumps. */
function EchoCard({ pattern, index }: { pattern: FounderBreakPattern; index: number }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="grid">
        {pattern.messages.map((message, position) => (
          <div
            key={`${message.from}-${position}`}
            className={cn(
              "[grid-area:1/1] transition-opacity duration-300",
              position === index ? "opacity-100" : "opacity-0",
            )}
          >
            <SceneQuote person={message.from} text={message.text} />
          </div>
        ))}
      </div>
      <p className="px-1 text-xs leading-4 text-white/55">{pattern.meta}</p>
    </div>
  );
}

const floodTilt = [-1.4, 0.8, -0.5, 1.1, -0.9, 0.6, -0.7];
const floodShift = ["0rem", "1.5rem", "0.5rem", "2rem", "0.75rem", "2.25rem", "1rem"];
const ARRIVE_MS = 850;
const VISIBLE_CARDS = 7;
/** The pile is already there when the step opens; new questions keep landing on top. */
const INITIAL_PILE = 4;
/** Lower cards sit a little closer together, smaller and darker, so the stack reads as a pile. */
const pileOffset = (position: number) => position - position * (position - 1) * 0.03;
/** The count climbs to here, then the pile clears and starts again, so a pinned step never runs away. */
const FLOOD_CEILING = 50;
/** Arrival labels follow the pile position, so the newest card is always on top. */
const arrivedLabels = ["Just now", "Today", "Today", "Yesterday", "Monday", "Last week", "2 weeks ago"];

/** `delay` staggers the opening pile so it lands bottom first. */
type FloodCard = { key: number; message: number; delay: number };

/** The opening pile, newest first: the opening question on top, the oldest few beneath it. */
function openingPile(firstKey: number, poolSize: number): FloodCard[] {
  return Array.from({ length: INITIAL_PILE }, (_, position) => ({
    key: firstKey + INITIAL_PILE - 1 - position,
    message: (poolSize - position) % poolSize,
    delay: (INITIAL_PILE - 1 - position) * 70,
  }));
}

/**
 * The questions themselves piling up beside a counter. A new question lands on
 * top every ARRIVE_MS and pushes the older ones down until they fade out. At
 * FLOOD_CEILING the pile clears and the opening pile lands again.
 */
function QuestionFlood({
  pattern,
  ticking,
  reducedMotion,
}: {
  pattern: FounderBreakPattern;
  ticking: boolean;
  reducedMotion: boolean;
}) {
  const pool = pattern.messages;
  const start = pattern.pile?.count ?? 0;
  const [flood, setFlood] = React.useState(() => ({ cards: openingPile(0, pool.length), count: start }));
  // Reduced motion shows a still pile of the first few questions instead of arrivals.
  const cards = reducedMotion
    ? pool.slice(0, 4).map((_, index) => ({ key: index, message: index, delay: 0 }))
    : flood.cards;
  const count = reducedMotion ? start : flood.count;

  React.useEffect(() => {
    if (!ticking) return;

    const timer = window.setInterval(() => {
      setFlood(({ cards: current, count: shown }) => {
        const key = (current[0]?.key ?? -1) + 1;

        if (shown >= FLOOD_CEILING) return { cards: openingPile(key, pool.length), count: start };

        const next = current.length === 0 ? 0 : (current[0].message + 1) % pool.length;
        return { cards: [{ key, message: next, delay: 0 }, ...current].slice(0, VISIBLE_CARDS), count: shown + 1 };
      });
    }, ARRIVE_MS);

    return () => window.clearInterval(timer);
  }, [ticking, pool.length, start]);

  return (
    <div className="absolute inset-0 grid grid-rows-[auto_minmax(0,1fr)] gap-5 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] sm:grid-rows-1 sm:items-center sm:gap-10">
      <div className="cooperly-swap-in sm:pl-4">
        <p className="text-6xl font-semibold leading-none tracking-[-0.05em] tabular-nums sm:text-8xl">
          <span key={count} className="cooperly-swap-in inline-block">
            {count}
          </span>
        </p>
        <p className="mt-3 max-w-[17rem] text-sm leading-5 text-white/60 sm:text-base sm:leading-6">
          {pattern.pile?.caption}
        </p>
        <span className="mt-5 hidden items-center gap-3 sm:inline-flex">
          {/* The same founder mark as the canvas steps: a capacity ring, nearly full. */}
          <span className="flex size-[1.375rem] items-center justify-center rounded-full border border-white/70 shadow-[0_0_24px_rgb(255_255_255/0.35)]">
            <span className="size-3.5 rounded-full bg-white" />
          </span>
          <span className="text-base font-medium text-white">You</span>
        </span>
      </div>

      <div className="relative h-full min-h-0 overflow-hidden [--slot:6.25rem] [mask-image:linear-gradient(to_bottom,black_50%,transparent)] sm:[--slot:5.25rem]">
        {cards.map((card, position) => {
          const message = pool[card.message];
          const person = contextStoryCast[message.from];
          const tilt = card.key % floodTilt.length;
          // The opening pile lands bottom first, a beat apart.
          const opening = reducedMotion ? 0 : card.delay;

          return (
            <div
              key={card.key}
              className="absolute inset-x-0 top-2 origin-top transition-[transform,filter] duration-500 ease-[var(--ease-out)] sm:top-6"
              style={{
                transform: `translateY(calc(var(--slot) * ${pileOffset(position)})) scale(${1 - position * 0.025})`,
                filter: `brightness(${1 - position * 0.07})`,
                zIndex: VISIBLE_CARDS - position,
              }}
            >
              <div
                className="max-w-[28rem]"
                style={{ marginLeft: floodShift[tilt], transform: `rotate(${floodTilt[tilt]}deg)` }}
              >
                <div
                  className="cooperly-pile-in flex items-start gap-3 rounded-2xl bg-white px-3.5 py-3 text-foreground shadow-[0_24px_48px_-24px_rgba(0,0,0,0.9)]"
                  style={opening ? { animationDelay: `${opening}ms` } : undefined}
                >
                  <CastAvatar person={message.from} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-3 text-xs leading-4 text-muted-foreground">
                      <span className="font-medium">{person.name}</span>
                      <span>{reducedMotion ? message.when : arrivedLabels[position]}</span>
                    </span>
                    <span className="mt-0.5 block text-sm font-medium leading-5">“{message.text}”</span>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Compact timeline labels for narrow screens. */
function shortWhen(label: string) {
  const compact: Record<string, string> = {
    March: "Mar",
    "6 weeks ago": "6 wk",
    "3 weeks ago": "3 wk",
    "Last week": "1 wk",
    Today: "Now",
  };
  return compact[label] ?? label;
}

/** The asker's photo; the name beside it carries the label, so the image stays decorative. */
function CastAvatar({
  person,
  muted = false,
  className,
}: {
  person: ContextStoryPersonId;
  muted?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "relative size-8 shrink-0 overflow-hidden rounded-full bg-muted transition-opacity ease-[var(--ease-out)]",
        muted && "opacity-45",
        className,
      )}
    >
      <Image src={contextStoryCast[person].avatarSrc} alt="" fill sizes="32px" className="object-cover" />
    </span>
  );
}

/** Runs a canvas scene engine: loops while animated, holds a still frame otherwise. */
function useSceneCanvas<Event = SceneEvent>(
  create: (canvas: HTMLCanvasElement, emit: (event: Event) => void) => SceneEngine | null,
  animate: boolean,
  reducedMotion: boolean,
  onEvent: (event: Event) => void,
) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const engineRef = React.useRef<SceneEngine | null>(null);
  const onEventRef = React.useRef(onEvent);

  React.useEffect(() => {
    onEventRef.current = onEvent;
  });

  React.useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const engine = create(canvas, (event) => onEventRef.current(event));

    if (!engine) return;

    engineRef.current = engine;
    engine.resize();
    engine.draw(0);

    const observer = new ResizeObserver(() => {
      engine.resize();
      engine.draw(performance.now() / 1000);
    });

    observer.observe(canvas);
    return () => {
      observer.disconnect();
      engineRef.current = null;
    };
  }, [create]);

  React.useEffect(() => {
    const engine = engineRef.current;

    if (!engine) return;

    if (!animate) {
      if (reducedMotion) engine.settle();
      engine.draw(performance.now() / 1000);
      return;
    }

    let frame = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      engine.step(dt);
      engine.draw(now / 1000);
      frame = requestAnimationFrame(loop);
    };

    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [animate, reducedMotion]);

  return canvasRef;
}

/** A quote card; a quiet one drops to an outline on the dark stage instead of turning grey. */
function SceneQuote({
  person,
  text,
  quiet,
}: {
  person: ContextStoryPersonId;
  text: string;
  quiet?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-2xl border px-3.5 py-3 transition-[background-color,border-color,color,box-shadow] duration-300 ease-[var(--ease-out)]",
        quiet
          ? "border-white/15 bg-transparent text-white/55 shadow-none"
          : "border-white bg-white text-foreground shadow-[0_24px_48px_-24px_rgba(0,0,0,0.9)]",
      )}
    >
      <CastAvatar person={person} muted={quiet} className="duration-300" />
      <span className="min-w-0">
        <span
          className={cn(
            "block text-xs font-medium leading-4 transition-colors duration-300",
            quiet ? "text-white/45" : "text-muted-foreground",
          )}
        >
          {contextStoryCast[person].name}
        </span>
        <span className="mt-0.5 block text-sm font-medium leading-5">“{text}”</span>
      </span>
    </div>
  );
}

/** 03: one ticket loops between Product and Engineering, then lands on the founder. */
function PingPongScene({
  pattern,
  animate,
  reducedMotion,
}: {
  pattern: FounderBreakPattern;
  animate: boolean;
  reducedMotion: boolean;
}) {
  const [beat, setBeat] = React.useState({
    count: 1,
    from: "product" as "product" | "engineering",
    escalated: false,
  });
  const canvasRef = useSceneCanvas(createPingPongEngine, animate, reducedMotion, (event) => {
    if (event.type === "bounce") setBeat({ count: event.count, from: event.from, escalated: false });
    if (event.type === "escalate") setBeat((current) => ({ ...current, escalated: true }));
  });
  const shown = reducedMotion ? { count: 3, from: "product" as const, escalated: false } : beat;
  const [productQuote, engineeringQuote] = pattern.messages;
  const percent = (value: number) => `${value * 100}%`;

  return (
    <div className="cooperly-swap-in absolute inset-0">
      <canvas ref={canvasRef} className="absolute inset-0 size-full" />

      {(
        [
          ["product", productQuote],
          ["engineering", engineeringQuote],
        ] as const
      ).map(([side, quote]) => (
        <div
          key={side}
          className="absolute hidden w-[15.5rem] -translate-x-1/2 sm:block"
          style={{ left: percent(PINGPONG[side].x), bottom: percent(1 - PINGPONG.quoteBottom) }}
        >
          <SceneQuote person={quote.from} text={quote.text} quiet={shown.escalated || shown.from !== side} />
        </div>
      ))}

      {(
        [
          ["product", "Product", productQuote.from],
          ["engineering", "Engineering", engineeringQuote.from],
        ] as const
      ).map(([side, label, person]) => (
        <div
          key={side}
          className="absolute -translate-x-1/2 text-center whitespace-nowrap"
          style={{ left: percent(PINGPONG[side].x), top: percent(PINGPONG[side].y + 0.09) }}
        >
          <p className="text-sm font-medium leading-5 text-white">{label}</p>
          <p className="text-xs leading-4 text-white/50">{contextStoryCast[person].name}</p>
        </div>
      ))}

      <div
        className="absolute -translate-x-1/2 -translate-y-1/2 text-center"
        style={{ left: percent(0.5), top: percent(PINGPONG.product.y) }}
      >
        <p className="text-5xl font-semibold leading-none tracking-[-0.05em] tabular-nums sm:text-7xl">
          <span key={shown.count} className="cooperly-swap-in inline-block">
            {shown.count}
          </span>
        </p>
        <p className="mt-2 text-xs leading-4 text-white/60 sm:text-sm sm:leading-5">
          {shown.escalated ? "then it lands on you" : "times reopened"}
        </p>
      </div>

      <span
        className="absolute -translate-y-1/2 text-sm font-medium text-white sm:text-lg"
        style={{ left: `calc(${percent(PINGPONG.founder.x)} + 1.5rem)`, top: percent(PINGPONG.founder.y) }}
      >
        You
      </span>
    </div>
  );
}

/** 04: an agreement dissolves along a timeline while the same question returns. */
function EchoScene({
  pattern,
  animate,
  reducedMotion,
  onEcho,
}: {
  pattern: FounderBreakPattern;
  animate: boolean;
  reducedMotion: boolean;
  onEcho: (index: number) => void;
}) {
  const [state, setState] = React.useState({ echoed: -1, decaying: false });
  const canvasRef = useSceneCanvas(createEchoEngine, animate, reducedMotion, (event) => {
    if (event.type === "reset") setState({ echoed: -1, decaying: false });
    if (event.type === "decay") setState((current) => ({ ...current, decaying: true }));
    if (event.type === "echo") {
      setState((current) => ({ ...current, echoed: event.index }));
      onEcho(event.index);
    }
  });
  const shown = reducedMotion ? { echoed: 1, decaying: true } : state;
  const percent = (value: number) => `${value * 100}%`;

  return (
    <div className="cooperly-swap-in absolute inset-0">
      <canvas ref={canvasRef} className="absolute inset-0 size-full" />

      {pattern.agreement ? (
        // The sealed card lets go of its fill at once; its outline then fades while nobody checks.
        <div className="absolute top-[5%] left-0 grid w-[11rem] sm:w-[14rem]">
          <div
            className={cn(
              "rounded-2xl border border-white bg-white px-3.5 py-3 text-foreground transition-opacity duration-300 ease-[var(--ease-out)] [grid-area:1/1]",
              shown.decaying ? "opacity-0" : "opacity-100",
            )}
          >
            <p className="text-xs font-medium leading-4 text-muted-foreground">Agreed in {pattern.agreement.when}</p>
            <p className="mt-0.5 text-sm font-medium leading-5">{pattern.agreement.text}</p>
          </div>
          <div
            aria-hidden="true"
            className={cn(
              "rounded-2xl border border-dashed px-3.5 py-3 [grid-area:1/1]",
              shown.decaying ? "border-white/15 text-white/40 opacity-100" : "border-white/35 text-white/75 opacity-0",
            )}
            style={{
              transition: shown.decaying
                ? "opacity 400ms var(--ease-out) 150ms, color 3600ms linear 500ms, border-color 3600ms linear 500ms"
                : "opacity 150ms var(--ease-out)",
            }}
          >
            <p className="text-xs font-medium leading-4">Agreed in {pattern.agreement.when}</p>
            <p className="mt-0.5 text-sm font-medium leading-5">{pattern.agreement.text}</p>
          </div>
        </div>
      ) : null}

      {pattern.messages.map((message, index) =>
        index <= shown.echoed ? (
          <div
            key={`${message.from}-${index}`}
            className="absolute hidden w-[14rem] lg:block"
            style={{
              left: percent(ECHO.echoes[index]),
              top: percent(ECHO.lineY - ECHO.lift[index % 2]),
              // The last question leans left of its stem so it clears the stage edge and "You".
              transform: `translate(${index === pattern.messages.length - 1 ? -78 : -50}%, -100%)`,
            }}
          >
            <div className="cooperly-drop-in">
              <SceneQuote person={message.from} text={message.text} />
            </div>
          </div>
        ) : null,
      )}

      {[
        { x: ECHO.agreement.x, label: pattern.agreement?.when ?? "", reached: true },
        ...pattern.messages.map((message, index) => ({
          x: ECHO.echoes[index],
          label: message.when ?? "",
          reached: index <= shown.echoed,
        })),
      ].map((tick) => (
        <span
          key={tick.label}
          className={cn(
            "absolute -translate-x-1/2 text-xs whitespace-nowrap transition-colors duration-300",
            tick.reached ? "text-white/80" : "text-white/35",
          )}
          style={{ left: percent(tick.x), top: percent(ECHO.lineY + 0.04) }}
        >
          <span className="hidden sm:inline">{tick.label}</span>
          <span className="sm:hidden">{shortWhen(tick.label)}</span>
        </span>
      ))}

      {/* Below the timeline beside the dates where there is room, above the founder on phones. */}
      <span
        className="absolute -translate-x-full text-sm font-medium text-white sm:hidden"
        style={{ left: percent(ECHO.founder.x + 0.02), top: percent(ECHO.lineY - 0.12) }}
      >
        You
      </span>
      <span
        className="absolute hidden -translate-x-1/2 text-sm leading-4 font-medium text-white sm:block"
        style={{ left: percent(ECHO.founder.x), top: percent(ECHO.lineY + 0.04) }}
      >
        You
      </span>
    </div>
  );
}
